// @flow
import PropTypes from "prop-types";
import LIButton from "../../GUI_COMMON/COMPONENTS/LIButton";
import React, {useRef} from "react";
import TextInput from "../../GUI_COMMON/COMPONENTS/TextInput";
import GUIUtils from "../../GUI_COMMON/SUPPORT/GuiUtils";

const ImageGenerator = ({onSaveDataBytes, fontSizePels=30, text="???", disabled=false} ) => {
    
    const IMAGE_WIDTH = 360;
    const IMAGE_HEIGHT = 240;
    
    const [showImageGenPopup, setShowImageGenPopup] = React.useState(false);
    
    /* this component is used to generate new images that are 360x240 and capable of
    * being stored as normal sample images in the Archiver */


    function adjustFontToFitWidth(fitToWidth: number, ctx:CanvasRenderingContext2D, text:string, fontSizePels:number) {        ctx.font = `${fontSizePels}px Arial`;

        const textWidth = ctx.measureText(text).width;
        if(textWidth > fitToWidth) {
            fontSizePels = Math.floor(fontSizePels * fitToWidth / textWidth);
        }
        // reset the font
        ctx.font = `${fontSizePels}px Arial`;
    }

    /*
    * This function creates a new image and saves it as a base64 string
    * (generated almost entirely by AI)
     */
    function generateImage(imageText:string, bgColor:string, fgColor:string) {
        
        const canvas = document.createElement('canvas');
        canvas.width = IMAGE_WIDTH;
        canvas.height = IMAGE_HEIGHT;
        const ctx = canvas.getContext('2d');
        
        ctx.fillStyle = bgColor;
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.strokeStyle = 'black';
        ctx.lineWidth = 5;
        ctx.roundRect(5, 5, canvas.width-10, canvas.height-10, 10);
        ctx.stroke();

        ctx.fillStyle = fgColor;
        // decide on the font size and where to put it
        ctx.font = `${fontSizePels}px Verdana`;
        const textHeight = GUIUtils.GetRenderedTextHeight(ctx, text);
        adjustFontToFitWidth(IMAGE_WIDTH-60, ctx, imageText, fontSizePels);
        ctx.textAlign = 'center';
        ctx.fillText(imageText, canvas.width / 2, canvas.height / 2 + textHeight/4);
        
        const dataURL = canvas.toDataURL('image/jpeg');
        const dataBytes = atob(dataURL.split(',')[1]);
        if(onSaveDataBytes) {
            onSaveDataBytes(dataBytes);
            canvas.remove();
        }
    }
    
    function showPopup() {
        setShowImageGenPopup(true);
    }
    
    function handleSaveClicked(imageText:string, bgColor:string, fgColor:string) {
        setShowImageGenPopup(false);
        generateImage(imageText, bgColor, fgColor);
    }
    
    function handleCancelClicked(e) {
        setShowImageGenPopup(false);
    }

    return (
        <div className="align-content-center">
            <LIButton faIcon="fa fa-paint-brush" disabled={disabled} onClick={showPopup}>Generate Image</LIButton>
            <ImageDataPopup show={showImageGenPopup} onSave={handleSaveClicked} onCancel={handleCancelClicked} initialText={text}/>
        </div>
    );
    
}

const ImageDataPopup = ({onSave, onCancel, show, initialText="???"}) => {

    const imageGeneratorRef = useRef(null);
    const [bgColor, setBgColor] = React.useState("gray");
    const [fgColor, setFgColor] = React.useState("black");
    const imageGeneratorBackdropRef = useRef(null);
    const [imageText, setImageText] = React.useState(initialText);

    function handleCloseClicked(e) {
        if (onCancel) {
            onCancel();
        }
    }

    function handleOkClicked(e) {
        if (onSave) {
            onSave(imageText, bgColor, fgColor);
        }
    }

    if (show && imageGeneratorBackdropRef?.current !== null) {
        imageGeneratorBackdropRef.current.style.display = "block";
        imageGeneratorRef.current.classList.remove("hide");
        imageGeneratorRef.current.classList.add("show");
        imageGeneratorRef.current.style.display = "block";
    } else if (imageGeneratorBackdropRef?.current !== null) { // hide
        imageGeneratorBackdropRef.current.style.display = "none";
        imageGeneratorRef.current.classList.remove("show");
        imageGeneratorRef.current.classList.add("hide");
        imageGeneratorRef.current.style.display = "none";
    }

    function handleColorChange(e) {
        if (e.target.id === "imageGenBgColor") {
            setBgColor(e.target.value);
        } else if (e.target.id === "imageGenFgColor") {
            setFgColor(e.target.value);
        }
    }

    function showColorSelector(id: string, defaultColor:string="white"): JSX.Element {
        const basicColors = [
            "black", "white", "red", "green", "blue", 
            "yellow", "purple", "orange", "brown", "pink", 
            "gray", "cyan", "magenta", "lime", "indigo", "teal"];

        // generate a dropdown selection list that has the 16 base colors in it
        const options = basicColors.map((color) => <option key={color} value={color}>{color}</option>);
        return (<select id={id} className="form-control text-center my-2" style={{width: "150px"}} name={id}
                        onChange={(e) => handleColorChange(e)}
                        defaultValue={defaultColor}>
                {options}
            </select>);
    }

    function handleOnChange(e) {
        if (e.target.id === "imageGenText") {
            setImageText(e.target.value);
        } else if (e.target.id === "imageGenBgColor") {
            setBgColor(e.target.value);
        } else if (e.target.id === "imageGenFgColor") {
            setFgColor(e.target.value);
        }
    }

    return (<>
            <div ref={imageGeneratorBackdropRef} className="modal-backdrop li-z-plus-1" id="imageGenBackdrop"/>
            <div ref={imageGeneratorRef} className={"modal fade"} tabIndex="-1"
                 id="imageDataPopup" role="dialog" aria-labelledby="imageGenPopup" aria-hidden="true">
                <div className="modal-dialog modal-dialog-centered" role="document">
                    <div className="modal-content li-modal-content">
                        <div className="modal-header li-modal-header" id="imageGenPopupHeader">
                            <h5 className="modal-title" id="imageGenPopupTitle">Generate Image</h5>
                            <button id="imageGenPopupClose" type="button" className="close" data-dismiss="modal"
                                    aria-label="close" onClick={(e) => handleCloseClicked(e)}>
                                <span>&times;</span>
                            </button>
                        </div>

                        <div className="modal-body mx-auto" id="imageGenPopupBody">
                            <table className="m-2" width="450px">
                                <tbody>
                                    <tr>
                                        <td className="text-right" style={{width: "200px"}}>Text:&nbsp;</td>
                                        <td>
                                            <TextInput id="imageGenText" label="" value={imageText} inputWidthPels={150}
                                                       widthPels={300}
                                                       onChange={(e) => handleOnChange(e)} name="imageGenText" oneRow/>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td className="text-right" style={{width: "100px"}}>Text Color:&nbsp;</td>
                                        <td>
                                            {showColorSelector("imageGenFgColor", fgColor)}
                                        </td>
                                    </tr>
                                    <tr>
                                        <td className="text-right" style={{width: "100px"}}>Background:&nbsp;</td>
                                        <td>
                                            {showColorSelector("imageGenBgColor", bgColor)}
                                        </td>
                                    </tr>
                                </tbody>
                            </table>

                        </div>
                        <div className="modal-footer border-0 w-100">
                            <button className="li-button li-primary" id="imageGenCloseBTN"
                                    onClick={(e) => handleCloseClicked(e)}>Close
                            </button>
                            <button className="li-button li-secondary" id="imageGenOkBTN"
                                    onClick={(e) => handleOkClicked(e)}>Save
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </>);
}
 
ImageGenerator.propTypes = {
    onSaveDataBytes: PropTypes.func.isRequired,
    bgColor: PropTypes.string,
    fgColor: PropTypes.string,
    fontSizePels: PropTypes.number,
    text: PropTypes.string.isRequired,
    disabled: PropTypes.bool
}

export default ImageGenerator;